/**
 * The history API serves intervals, not point samples: each row spans a period
 * during which state held constant, and `samples` counts the polls that
 * confirmed it. Anything drawn over time is therefore a step function, and a
 * one-sample interval is a single poll rather than a sustained state.
 */
export type Observation = {
	from: number;
	to: number;
	samples: number;
	priceMinor: number | null;
	listPriceMinor: number | null;
	inStock: boolean;
	stockLevel: number | null;
	availabilityRaw: string | null;
	backorder: boolean;
	preorder: boolean;
	maxOrderQty: number | null;
};

/**
 * Fixed 30 days, matching the history endpoint's own default range. Fixed
 * rather than per-product so rows in a list are comparable with each other;
 * comparability matters more in a list than per-row detail does.
 */
export const STRIP_RANGE_MS = 30 * 24 * 60 * 60_000;

/** Trim intervals to the visible window, clamping the partial one at the edge. */
export function clipToRange(obs: Observation[], now: number, rangeMs: number): Observation[] {
	const start = now - rangeMs;
	return obs
		.filter((o) => o.to > start && o.from < now)
		.map((o) => ({
			...o,
			from: Math.max(o.from, start),
			to: Math.min(o.to, now),
		}));
}
